import {
    useState,
    forwardRef,
    useImperativeHandle,
} from 'react'

const countryOptions = [
    { label: 'United States', code: '1', nsnLength: { min: 10, max: 10 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Canada', code: '1', nsnLength: { min: 10, max: 10 }, extensionLength: { min: 1, max: 5 } },
    { label: 'India', code: '91', nsnLength: { min: 10, max: 10 }, extensionLength: { min: 1, max: 6 } },
    { label: 'United Kingdom', code: '44', nsnLength: { min: 9, max: 10 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Australia', code: '61', nsnLength: { min: 9, max: 9 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Germany', code: '49', nsnLength: { min: 7, max: 11 }, extensionLength: { min: 1, max: 5 } },
    { label: 'France', code: '33', nsnLength: { min: 9, max: 9 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Japan', code: '81', nsnLength: { min: 10, max: 10 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Brazil', code: '55', nsnLength: { min: 10, max: 11 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Russia', code: '7', nsnLength: { min: 10, max: 10 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Mexico', code: '52', nsnLength: { min: 10, max: 10 }, extensionLength: { min: 1, max: 6 } },
    { label: 'South Africa', code: '27', nsnLength: { min: 9, max: 9 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Italy', code: '39', nsnLength: { min: 9, max: 10 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Spain', code: '34', nsnLength: { min: 9, max: 9 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Netherlands', code: '31', nsnLength: { min: 9, max: 9 }, extensionLength: { min: 1, max: 5 } },
    { label: 'China', code: '86', nsnLength: { min: 11, max: 11 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Argentina', code: '54', nsnLength: { min: 10, max: 10 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Belgium', code: '32', nsnLength: { min: 8, max: 9 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Sweden', code: '46', nsnLength: { min: 9, max: 9 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Switzerland', code: '41', nsnLength: { min: 9, max: 9 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Norway', code: '47', nsnLength: { min: 8, max: 8 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Denmark', code: '45', nsnLength: { min: 8, max: 8 }, extensionLength: { min: 1, max: 5 } },
    { label: 'New Zealand', code: '64', nsnLength: { min: 8, max: 8 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Singapore', code: '65', nsnLength: { min: 8, max: 8 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Ireland', code: '353', nsnLength: { min: 7, max: 9 }, extensionLength: { min: 1, max: 5 } },
    { label: 'South Korea', code: '82', nsnLength: { min: 9, max: 10 }, extensionLength: { min: 1, max: 5 } },
    { label: 'Turkey', code: '90', nsnLength: { min: 10, max: 10 }, extensionLength: { min: 1, max: 5 } },
]

export default forwardRef(({ data, readOnly = false, isMandatory = true, title }, ref) => {

    // ✅ Determine initial country without mutating `data`
    let initialCountry = countryOptions[0]
    if (data?.countryCode) {
        const match = countryOptions.find(option => String(option.code) === String(data.countryCode))
        if (match) {
            initialCountry = match
        }
    }

    const phoneNumberData = {
        countryCode: initialCountry,
        number: data?.number || '',
        extension: data?.extension || '',
    }

    const [selectedCountry, setSelectedCountry] = useState(phoneNumberData.countryCode)
    const [phone, setPhone] = useState(phoneNumberData.number)
    const [extension, setExtension] = useState(phoneNumberData.extension)
    const [error, setError] = useState(false)

    const formatPhone = (value) => {
        if (!value) return ""
        return value.replace(/\D/g, '')
    }

    useImperativeHandle(ref, () => ({
        validateAndGetData: () => {
            const digitsOnly = formatPhone(phone)
            let isValid = digitsOnly.length >= selectedCountry.nsnLength.min && digitsOnly.length <= selectedCountry.nsnLength.max
            if (!isMandatory && !phone) {
                isValid = true
            }
            setError(!isValid)

            return {
                isValid,
                value: {
                    countryCode: selectedCountry.code,
                    number: digitsOnly,
                    extension: formatPhone(extension),
                },
            }
        },
    }))

    const placeHolderForDigit = selectedCountry?.nsnLength?.min === selectedCountry?.nsnLength?.max ?
        `Enter ${selectedCountry?.nsnLength?.min}-digit number` :
        `Enter ${selectedCountry?.nsnLength?.min} to ${selectedCountry?.nsnLength?.max}-digit number`

    return (
        <div className="mb-5">
            <label className="block text-sm font-medium text-gray-700 mb-1">
                {title ? `${title}${isMandatory ? "*" : ""}` : `Phone Number${isMandatory ? "*" : ""}`}
            </label>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-2">
                <select
                    className="border border-gray-300 rounded-md p-2 bg-white w-full col-span-1"
                    value={selectedCountry.code}
                    disabled={readOnly}
                    onChange={(e) => {
                        const selected = countryOptions.find(c => c.code === e.target.value)
                        setSelectedCountry(selected)
                        setPhone('')
                        setError(false)
                    }}
                >
                    {countryOptions.map((country) => (
                        <option key={country.label} value={country.code}>
                            {country.label} (+{country.code})

                        </option>
                    ))}
                </select>

                <div className="flex items-center border border-gray-300 rounded-md px-3 bg-white w-full col-span-2">
                    <span className="text-gray-700">+{selectedCountry.code}</span>
                    <input
                        type="text"
                        inputMode="numeric"
                        value={phone}
                        disabled={readOnly}
                        onChange={(e) => setPhone(formatPhone(e.target.value))}
                        maxLength={selectedCountry.nsnLength.max}
                        className="ml-2 flex-1 outline-none py-2"
                        placeholder={placeHolderForDigit}
                    />
                </div>

                <input
                    type="text"
                    inputMode="numeric"
                    value={extension}
                    onChange={(e) => setExtension(formatPhone(e.target.value))}
                    maxLength={selectedCountry.extensionLength.max}
                    disabled={readOnly}
                    placeholder={"Ext."}
                    className="w-full rounded-md border border-gray-300 px-3 bg-white col-span-1 py-2"
                />
            </div>

            {error && !readOnly && (
                <p className="mt-1 text-sm text-red-600">
                    Enter a valid phone number.
                </p>
            )}
        </div>
    )
})
